import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LoadingScreen,
} from "@eduviaos/ui-kit";
import { ApiError, type SupportSessionExchangeResponse } from "@eduviaos/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { exchangeOnce, takeSupportCodeFromHash } from "@/auth/supportSession";

/**
 * Phase SC-22 — landing route for "Open centre admin" from the SA portal.
 *
 * The one-time code arrives in the URL fragment (never sent to a server),
 * is stripped from the address bar before anything else runs, and is
 * exchanged exactly once for a support token held only by this tab.
 */
export function SupportSessionPage() {
  const navigate = useNavigate();
  const { beginSupportSession } = useAuth();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    const code = takeSupportCodeFromHash();
    if (!code) {
      setError("This support link is missing its sign-in code.");
      return;
    }
    exchangeOnce(code, (value) =>
      api.post<SupportSessionExchangeResponse>("/auth/support-session/exchange", { code: value }),
    )
      .then((resp) => beginSupportSession(resp))
      .then(() => {
        if (active) navigate("/dashboard", { replace: true });
      })
      .catch((err: unknown) => {
        if (active) setError(describeExchangeError(err));
      });
    return () => {
      active = false;
    };
  }, [beginSupportSession, navigate]);

  if (!error) return <LoadingScreen label="Opening support view…" />;

  return (
    <main className="bg-background flex min-h-full items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="bg-warning/15 text-warning-foreground mb-2 flex size-10 items-center justify-center rounded-full">
            <ShieldAlert className="size-5" aria-hidden="true" />
          </div>
          <CardTitle>Couldn&apos;t open the support view</CardTitle>
          <CardDescription role="alert">{error}</CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          Support links work once and expire after a minute. Start a new session from the super
          admin portal, then close this tab.
        </CardContent>
      </Card>
    </main>
  );
}

function describeExchangeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "This support link has expired or was already used.";
    if (err.code === "TENANT_MISMATCH") return err.message;
    if (err.status === 0) return "Couldn't reach EduViaOS. Check your connection and retry.";
  }
  return "Something went wrong while opening the support view.";
}
